import { PromoCodeViewType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { validAllowedCharacters, validateLengthRange, validIsUnique } from 'src/api/utils/form2Validators';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import { isNumericWholeNumber } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.validators';
import { computed, makeAutoObservable } from 'mobx';
import { v4 as uuid } from 'uuid';
import { CampaignStore } from 'src/domains/casino/state/AppCasino/store/campaign-store/Campaign.store';
import { Common } from 'src/AppState/Common';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { CampaignCreatorState } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.state';

export type SinglePromoCodeFormType = {
    promoCodeName: string;
    useLimit: string;
};

export class SinglePromoCodeState {
    public readonly id: string;
    public readonly promoCodeName: FormInputState<string, string>;
    public readonly useLimit: FormInputState<string, string>;
    private readonly form: FormModel<SinglePromoCodeFormType>;
    public isPaused: boolean = false;
    private campaignStore: CampaignStore;

    public constructor(
        private readonly common: Common,
        private readonly promoCodeData: PromoCodeViewType | null,
        private getCodeNames: () => Array<string>,
        private campaignId?: string | null
    ) {
        makeAutoObservable(this);
        this.campaignStore = CampaignStore.get(this.common);

        this.id = uuid();
        this.promoCodeName = FormInputState.new(this.promoCodeData?.promoCode ?? '')
            .map((value) => {
                if (typeof this.campaignId === 'string') {
                    const optInOnly = CampaignCreatorState.get(this.common, this.campaignId).generalDetailsState
                        .optInOnly;
                    if (optInOnly === true) {
                        return Result.createError(
                            `Promo Code can not be enabled when 'Require User Opt-In' is selected`
                        );
                    }
                }

                return Result.createOk(value);
            })
            // @ts-expect-error
            .map(validateNotEmpty('Required field'))
            .map(validateLengthRange({ max: 100, min: 2 }))
            .map(validAllowedCharacters(/^[a-zA-Z0-9]*$/, 'Use only letters and numbers'))
            .map(validIsUnique(this.getCodeNames));

        this.useLimit = FormInputState.new(this.promoCodeData?.useLimit?.toString() ?? '').map(
            isNumericWholeNumber(true, 'Enter a number. Minimum value is 1')
        );
        this.isPaused = this.promoCodeData?.isPaused ?? false;

        this.form = FormModel.group({
            promoCodeName: this.promoCodeName,
            useLimit: this.useLimit,
        });
    }

    public get isPromoCodeEmpty(): boolean {
        return this.promoCodeName.value === '' && this.useLimit.value === '';
    }

    public isTouched(): boolean {
        return this.form.isTouched() || (this.promoCodeName.value === '' && this.useLimit.value === '');
    }

    public setIsTouched(): void {
        this.form.setIsTouched();
    }

    public resetIsTouched(): void {
        this.form.resetIsTouched();
    }

    public setIsVisited(): void {
        this.form.setAsVisited();
    }

    public resetIsVisited(): void {
        this.form.resetIsVisited();
    }

    @computed public get isFormValid(): boolean {
        return this.form.result.value.type === 'ok';
    }

    @computed public get formErrorsCount(): number {
        return this.form.errorsForView.length;
    }

    public setPromoActive = async (campaignId?: string): Promise<void> => {
        await this.updatePromoCode(campaignId, false);
    };

    public setPromoInactive = async (campaignId?: string): Promise<void> => {
        await this.updatePromoCode(campaignId, true);
    };

    private updatePromoCode = async (campaignId: string | undefined, isPaused: boolean): Promise<void> => {
        if (campaignId === undefined) {
            console.error(`setPromoActive - campaignId is undefined`);
            return;
        }

        await this.campaignStore.updatePromoCode(campaignId, this.promoCodeData?.promoCode ?? '', {
            isPaused,
            useLimit: this.promoCodeData?.useLimit,
        });
    };
}
